/** Matches packages/shared/src/schemas.ts — z.string().min(12). */
export const MIN_PASSWORD_LENGTH = 12

const LABELS = ['Too short', 'Weak', 'Fair', 'Good', 'Strong'] as const

/**
 * Zero below the server's minimum, whatever the character mix. The handoff scored length >= 8
 * as a filled segment, which would show "Good" for a password the API rejects with a 400.
 */
export function scorePassword(password: string): number {
  if (password.length < MIN_PASSWORD_LENGTH) return 0
  let score = 0
  if (/[A-Z]/.test(password)) score += 1
  if (/[a-z]/.test(password)) score += 1
  if (/[0-9]/.test(password)) score += 1
  if (/[^A-Za-z0-9]/.test(password)) score += 1
  return score
}

function toneFor(score: number): string {
  if (score <= 1) return 'var(--negative)'
  if (score === 2) return 'var(--warning)'
  return 'var(--positive)'
}

export function PasswordStrength({ password }: { readonly password: string }) {
  if (password === '') return null

  const score = scorePassword(password)
  const tone = toneFor(score)

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 4 }}>
        {[0, 1, 2, 3].map((index) => {
          const filled = index < score
          return (
            <div
              key={index}
              data-testid="password-strength-segment"
              data-filled={filled ? 'true' : 'false'}
              style={{
                flex: 1,
                height: 3,
                borderRadius: 2,
                background: filled ? tone : 'var(--border)',
              }}
            />
          )
        })}
      </div>
      <div
        data-testid="password-strength-label"
        aria-live="polite"
        style={{
          marginTop: 6,
          fontSize: 11,
          textAlign: 'right',
          color: score === 0 ? 'var(--fg-muted)' : tone,
        }}
      >
        {LABELS[score]}
      </div>
    </div>
  )
}
